import * as React from 'react';
import * as _ from 'lodash';
import * as actions from '../../../../redux/corpus/actions.corpus';
import { ConnectedProps, connect } from 'react-redux';
import { IStore } from '../../../../redux/initialState';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import TextFieldWrapper from '../../../components/TextFieldWrapper/TextFieldWrapper';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ISearchByCorpusParam } from '../../../../redux/corpus/actions.corpus';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import ComboBox from '../../../components/ComboBox/ComboBox';
import { alanMarkdown } from '../../../../../../alan_btn/src/textChat/alanMarkdown';


const values = [
    { key: '512', label: '512' },
    { key: '768', label: '768' },
    { key: '1024', label: '1024' },
    { key: '2048', label: '2048' },
    { key: '3072', label: '3072' },
    { key: '4096', label: '4096' },
    { key: '5120', label: '5120' },
    { key: '6144', label: '6144' },
];


type CorpusDocumentBodyProps = PropsFromRedux & {
    selectedFormat: string
};

function CorpusDocumentBody({
    document,
    selectedFormat,
    searchByCorpus,
    clearCorpusSearch,
    searchInProcess,
    corpusSearchQuery,
    corpusId
}: CorpusDocumentBodyProps) {
    const [query, setQuery] = useState('');
    const [question, setQuestion] = useState('');
    const [selectedMaxToken, setSelectedMaxToken] = useState('1024');

    const lastQuery = useRef();
    const lastQuestion = useRef();
    const lastMaxTokens = useRef();


    useEffect(() => {
        if (corpusSearchQuery === '') {
            setQuery('');
        }
    }, [corpusSearchQuery]);

    const debouncedSearch = useCallback(_.debounce((newQuery, newQuestion, maxTokens) => {
        const trimmedQuery = newQuery.trim();
        const trimmedQuestion = newQuestion.trim();

        if ((lastQuery?.current !== trimmedQuery ||
            lastQuestion?.current !== trimmedQuestion ||
            lastMaxTokens?.current !== maxTokens
        )) {

            lastQuestion.current = trimmedQuestion;
            lastQuery.current = trimmedQuery;
            lastMaxTokens.current = maxTokens;

            if (trimmedQuery === '' && trimmedQuestion === '') {
                clearCorpusSearch();
            } else {
                searchByCorpus({ corpusId, query: trimmedQuery, question: trimmedQuestion, maxTokens });
            }
        }
    }, 2000), []);

    const handleQueryChange = useCallback((e) => {
        const newVal = e.target.value;
        setQuery(newVal);
        debouncedSearch(newVal, question, selectedMaxToken);
    }, [debouncedSearch, question, selectedMaxToken]);


    const handleQuestionChange = useCallback((e) => {
        const newVal = e.target.value;
        setQuestion(newVal);
        debouncedSearch(query, newVal, selectedMaxToken);
    }, [debouncedSearch, query, selectedMaxToken]);


    const handleQueryClose = useCallback(() => {
        setQuery('');
        debouncedSearch('', question, selectedMaxToken);
    }, [clearCorpusSearch, question, selectedMaxToken]);


    const handleQuestionClose = useCallback(() => {
        setQuestion('');
        debouncedSearch(query, '', selectedMaxToken);
    }, [clearCorpusSearch, query, selectedMaxToken]);

    const handleMaxTokenChange = ({ key }) => {
        if (key !== selectedMaxToken) {
            setSelectedMaxToken(key);
            debouncedSearch(query, question, key);
        }
    };

    return (
        <div className="corpus-document-body">

            {document === null ?
                <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} center={true} appearWithFade={true} />
                :
                <>
                    {searchInProcess && <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} center={true} appearWithFade={true} />}
                    <div className={`corpus-document-body__search-panel ${searchInProcess && 'disabled'}`}>
                        <div className="corpus-document-body__search">
                            <i className="icon icon-alan-search" />
                            <TextFieldWrapper
                                name="query"
                                id="query"
                                placeholder="Search"
                                value={query}
                                autoComplete="off"
                                onChange={handleQueryChange}
                                onBlur={() => { }}
                                onEsc={handleQueryClose}
                                skipOnChangeAfterOnBlur={true}
                                height={30}
                            />
                            {query && <i className="icon icon-alan-close-rounded" onClick={handleQueryClose} />}
                        </div>
                        <div className="corpus-document-body__search">
                            <i className="icon icon-alan-question" />
                            <TextFieldWrapper
                                name="question"
                                id="question"
                                placeholder="Question"
                                value={question}
                                autoComplete="off"
                                onChange={handleQuestionChange}
                                onBlur={() => { }}
                                onEsc={handleQuestionClose}
                                skipOnChangeAfterOnBlur={true}
                                height={30}
                            />
                            {question && <i className="icon icon-alan-close-rounded" onClick={handleQuestionClose} />}
                        </div>
                        <div className="corpus-document-body__cb-wrapper">
                            <label>Max tokens:</label>
                            <ComboBox
                                values={values}
                                valueKey="key"
                                valueName="label"
                                width="70px"
                                selectedValueId={selectedMaxToken}
                                renderItem={(item) => {
                                    return <div style={{ paddingLeft: '8px' }}>{item.label}</div>;
                                }}
                                onValueSelect={handleMaxTokenChange}
                            />
                        </div>
                    </div>
                    {(document?.content && document?.content.length > 0) ?
                        <div className={`corpus-document-body__content-text-viewer ${selectedFormat === 'markdown' ? 'with-markdown studio-markdown' : 'row-text'}`}>
                            {selectedFormat === 'markdown' ?
                                <span dangerouslySetInnerHTML={{ __html: alanMarkdown(document?.content.join('') || '') }}></span> :
                                document?.content.map((el, i) => {
                                    return el.indexOf('alan-search-separator') > -1 ? <span key={i} dangerouslySetInnerHTML={{ __html: el }}></span> : el;
                                })
                            }
                        </div>
                        :
                        <div className="corpus-document-body__no-data-for-viewer">
                            No content for display
                        </div>}
                </>
            }
        </div>
    );
}

function mapStateToProps({ corpus }: IStore, ownProps) {
    return {
        document: corpus.document,
        corpusId: corpus.corpusId,
        searchInProcess: corpus.searchInProcess,
        corpusSearchQuery: corpus.corpusSearchQuery,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        searchByCorpus(param: ISearchByCorpusParam): Promise<any> {
            return asyncDispatch(actions.searchByCorpus, dispatch, param);
        },
        clearCorpusSearch(): Promise<any> {
            return asyncDispatch(actions.clearCorpusSearch, dispatch);
        },

    };
}


const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CorpusDocumentBody);